import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Loader2, Filter, ChevronDown, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import CampaignCard from '../campaigns/CampaignCard';
import LocationPicker from '../ui/LocationPicker';
import { campaignAPI } from '../../services/api';
import { featuredCampaigns as mockCampaigns, categories } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

const FeaturedCampaigns = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [preferencesApplied, setPreferencesApplied] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  // Multi-select categories
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  
  // Apply user preferences when user loads
  useEffect(() => {
    if (user && !preferencesApplied) {
      if (user.campaign_preferences && user.campaign_preferences.length > 0) {
        setSelectedCategories(user.campaign_preferences);
        setPreferencesApplied(true);
      } else if (user.preferred_category) {
        setSelectedCategories([user.preferred_category]);
        if (user.preferred_subcategory) {
          setSelectedSubcategory(user.preferred_subcategory);
        }
        setPreferencesApplied(true);
      }
    }
  }, [user, preferencesApplied]);

  // Get sorted categories and subcategories
  const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name));
  const selectedCategoryData = selectedCategories.length === 1 
    ? categories.find(c => c.id === selectedCategories[0]) 
    : null;
  const subcategories = [...(selectedCategoryData?.subcategories || [])].sort((a, b) => a.localeCompare(b));

  // Toggle category selection
  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => {
      const isSelected = prev.includes(categoryId);
      if (isSelected) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
    setSelectedSubcategory('all');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCategoryDropdown && !event.target.closest('.category-dropdown-home')) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCategoryDropdown]);

  // Fetch campaigns when filters change
  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoading(true);
      try {
        const params = { limit: 6 };
        // Support multi-select categories
        if (selectedCategories.length > 0) {
          params.category = selectedCategories.join(',');
        }
        if (selectedSubcategory !== 'all') params.subcategory = selectedSubcategory;
        
        const data = await campaignAPI.list(params);
        // Map API response to frontend format
        const formattedCampaigns = data.map(c => ({
          id: c.id,
          title: c.title,
          story: c.story,
          goal: c.goal,
          raised: c.raised,
          donors: c.donors,
          category: c.category,
          subcategory: c.subcategory,
          image: c.image,
          creator: c.creator,
          location: c.location,
          country: c.country || 'US',
          daysLeft: c.days_left,
          verified: c.verified,
          escrow: c.escrow,
          trending: c.trending,
          milestones: c.milestones,
        }));
        const campaignsToUse = formattedCampaigns.length > 0 ? formattedCampaigns : mockCampaigns.slice(0, 6);
        setCampaigns(campaignsToUse);
      } catch (error) {
        console.error('Failed to fetch campaigns:', error);
        setCampaigns(mockCampaigns.slice(0, 6));
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [selectedCategories, selectedSubcategory]);

  // Filter campaigns based on location and selected categories (client-side)
  const filteredCampaigns = React.useMemo(() => {
    let result = [...campaigns];
    
    // Filter by selected categories if any
    if (selectedCategories.length > 0) {
      result = result.filter(c => selectedCategories.includes(c.category));
    }
    
    if (selectedLocation) {
      const searchCity = selectedLocation.city?.toLowerCase() || '';
      const searchCountry = selectedLocation.country?.toLowerCase() || '';
      
      result = result.filter(c => {
        const campaignLocation = (c.location || '').toLowerCase();
        const campaignCountry = (c.country || '').toLowerCase();
        
        return campaignLocation.includes(searchCity) || 
               campaignCountry.includes(searchCountry) ||
               campaignLocation.includes(searchCountry);
      });
    }
    
    return result;
  }, [campaigns, selectedLocation, selectedCategories]);

  return (
    <section className="py-12 bg-secondary/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-medium text-amber-500 uppercase tracking-wide">{t('featured')}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              {t('campaignsMakingImpact')}
            </h2>
            <p className="text-foreground/70 mt-2 max-w-lg">
              {t('featuredSubtitle')}
            </p>
          </div>
          <Link to="/discover?all=true" className="mt-4 sm:mt-0">
            <Button variant="outline" className="rounded-full">
              {t('seeAllCampaigns')} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>

        {/* Filter Bar with Category, Subcategory, Location - Auto-search on selection */}
        <div className="bg-card border border-border rounded-xl p-4 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            {/* Multi-Select Category Dropdown */}
            <div className="md:col-span-4 relative category-dropdown-home">
              <label className="text-xs font-semibold text-foreground/80 mb-1 block">{t('category')}</label>
              <button
                type="button"
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="w-full flex items-center justify-between px-3 py-2 bg-background border-2 border-emerald-500/40 rounded-md text-sm"
              >
                <span className={selectedCategories.length > 0 ? 'text-foreground' : 'text-foreground/60'}>
                  {selectedCategories.length === 0 
                    ? t('allCategories')
                    : selectedCategories.length === 1
                      ? categories.find(c => c.id === selectedCategories[0])?.name
                      : `${selectedCategories.length} selected`}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showCategoryDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  <div className="p-2 border-b border-border flex justify-between items-center">
                    <span className="text-xs text-foreground/70">{t('selectMultiple') || 'Select multiple'}</span>
                    {selectedCategories.length > 0 && (
                      <button
                        onClick={() => { setSelectedCategories([]); setShowCategoryDropdown(false); }}
                        className="text-xs text-emerald-500 hover:text-emerald-400"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  {sortedCategories.map((category) => {
                    const isSelected = selectedCategories.includes(category.id);
                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => toggleCategory(category.id)}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-secondary/50 ${
                          isSelected ? 'bg-emerald-500/10 text-emerald-400' : 'text-foreground'
                        }`}
                      >
                        <span>{category.name}</span>
                        {isSelected && <Check className="w-4 h-4 text-emerald-500" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Subcategory Dropdown */}
            <div className="md:col-span-4">
              <label className="text-xs font-semibold text-foreground/80 mb-1 block">{t('subcategory')}</label>
              <Select 
                value={selectedSubcategory} 
                onValueChange={setSelectedSubcategory}
                disabled={selectedCategories.length !== 1 || subcategories.length === 0}
              >
                <SelectTrigger className="bg-background border-2 border-emerald-500/40">
                  <SelectValue placeholder={t('allSubcategories')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('allSubcategories')}</SelectItem>
                  {subcategories.map((sub) => (
                    <SelectItem key={sub} value={sub}>
                      {sub}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location Input */}
            <div className="md:col-span-4">
              <label className="text-xs font-semibold text-foreground/80 mb-1 block">{t('location')}</label>
              <LocationPicker
                value={selectedLocation?.display || ''}
                onChange={setSelectedLocation}
                placeholder={t('cityOrCountry')}
              />
            </div>
          </div>

          {/* Active Filters Summary */}
          {(selectedCategories.length > 0 || selectedSubcategory !== 'all' || selectedLocation) && (
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
              <span className="text-xs font-semibold text-foreground/80">{t('activeFilters')}:</span>
              {selectedCategories.map(catId => {
                const cat = categories.find(c => c.id === catId);
                return cat ? (
                  <Badge key={catId} variant="secondary" className="flex items-center gap-1 text-xs bg-emerald-500/20 text-emerald-400">
                    {cat.name}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => toggleCategory(catId)} />
                  </Badge>
                ) : null;
              })}
              {selectedSubcategory !== 'all' && (
                <Badge variant="secondary" className="flex items-center gap-1 text-xs bg-teal-500/20 text-teal-400">
                  {selectedSubcategory}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedSubcategory('all')} />
                </Badge>
              )}
              {selectedLocation && (
                <Badge variant="secondary" className="flex items-center gap-1 text-xs bg-secondary text-muted-foreground">
                  📍 {selectedLocation.display}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedLocation(null)} />
                </Badge>
              )}
              {preferencesApplied && (user?.campaign_preferences?.length > 0 || user?.preferred_category) && (
                <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full flex items-center gap-1">
                  <Filter className="w-3 h-3" /> Your Preferences
                </span>
              )}
              <Button 
                variant="ghost" 
                size="sm"
                className="text-xs h-6 text-foreground/70 hover:text-foreground"
                onClick={() => {
                  setSelectedCategories([]);
                  setSelectedSubcategory('all');
                  setSelectedLocation(null);
                  setPreferencesApplied(false);
                }}
              >
                {t('clearAll')}
              </Button>
            </div>
          )}
        </div>

        {/* Campaigns Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : filteredCampaigns.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCampaigns.map((campaign) => (
              <CampaignCard key={campaign.id} campaign={campaign} variant="featured" />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-card rounded-xl border border-border">
            <p className="text-muted-foreground mb-4">{t('noCampaignsFound')}</p>
            <Button 
              variant="outline"
              onClick={() => {
                setSelectedCategories([]);
                setSelectedSubcategory('all');
                setSelectedLocation(null);
              }}
            >
              {t('clearAll')} {t('filters') || 'Filters'}
            </Button>
          </div>
        )}

        {/* Load More */}
        <div className="text-center mt-12">
          <Link to="/discover">
            <Button size="lg" variant="outline" className="rounded-full px-8">
              Explore All Campaigns
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedCampaigns;
